import React, { useState, useRef } from 'react';
import { Box, Tooltip, IconButton } from '@mui/material';
import { motion } from 'framer-motion';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';

const InteractiveVideoPlayer = ({ mediaUrl, infoPoint, verifyUrl, shieldIcon }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const videoRef = useRef(null);

    const togglePlay = () => {
        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleEnded = () => {
        setIsPlaying(false);
    };

    return (
        <Box sx={{ 
            width: '100%', height: '100%', minHeight: '280px', position: 'relative', 
            backgroundColor: '#000', borderRadius: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
            {/* Video Element */}
            <video 
                ref={videoRef} 
                src={mediaUrl} 
                onEnded={handleEnded} 
                style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
                    objectFit: 'cover', opacity: isPlaying ? 1 : 0.6, transition: 'opacity 0.3s ease'
                }}
            />

            {/* Central Play/Pause Button */}
            <IconButton 
                onClick={togglePlay}
                aria-label={isPlaying ? "Pause video" : "Play video"}
                sx={{ 
                    zIndex: 5, 
                    color: isPlaying ? 'rgba(255,255,255,0.1)' : 'white', 
                    transition: 'all 0.2s',
                    '&:hover': { transform: 'scale(1.1)', backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' } 
                }}
            >
                {isPlaying ? (
                    <PauseCircleOutlineIcon sx={{ fontSize: 80, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.8))' }} />
                ) : (
                    <PlayCircleOutlineIcon sx={{ fontSize: 80, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.8))' }} />
                )}
            </IconButton>

            {/* Top Left Static Info Point */}
            {infoPoint && (
                <Tooltip
                    placement={infoPoint.position || 'right'} 
                    arrow
                    disableFocusListener
                    disableTouchListener={false}
                    enterTouchDelay={0}
                    leaveTouchDelay={3000}
                    componentsProps={{
                        tooltip: { sx: { bgcolor: '#171F29', border: '1px solid var(--secondary-color)', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', p: 1.5, maxWidth: 380 } },
                        arrow: { sx: { color: '#171F29' } }
                    }}
                    title={
                        <div style={{ textAlign: 'left', fontFamily: 'Roboto, sans-serif' }}>
                            <div style={{ fontWeight: 700, marginBottom: '10px', fontSize: '0.75rem', color: 'var(--secondary-color)', letterSpacing: '0.5px' }}>{infoPoint.title}</div>
                            <div style={{ color: '#cbd5e1', fontSize: '0.85rem', lineHeight: 1.5 }}>{infoPoint.reason}</div>
                        </div>
                    }
                >
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.4, type: 'spring' }}
                        style={{ 
                            position: 'absolute', top: '4px', left: '4px', width: '26px', height: '26px', 
                            backgroundColor: 'var(--secondary-color)', border: '2px solid white', borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'help', zIndex: 10,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.5)',
                        }}
                    >
                        <span style={{ color: 'white', fontSize: '15px', fontWeight: 'bold', fontFamily: 'serif', marginTop: '-1px' }}>i</span>
                    </motion.div>
                </Tooltip>
            )}

            {/* Top Right Verification Shield */}
            {verifyUrl && shieldIcon && (
                <Tooltip
                    placement="left" arrow
                    componentsProps={{
                        tooltip: { sx: { bgcolor: '#171F29', boxShadow: '0 8px 32px rgba(0,0,0,0.9)', padding: '8px 12px', border: '1px solid rgba(255,255,255,0.1)' } },
                        arrow: { sx: { color: '#171F29' } }
                    }}
                    title={<div style={{ color: 'white', fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.5px' }}>View Verification Page</div>}
                >
                    <motion.div
                        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.6, type: 'spring' }}
                        onClick={() => window.open(verifyUrl, '_blank')}
                        style={{
                            position: 'absolute', top: '4px', right: '4px', cursor: 'pointer', zIndex: 10,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.filter = 'brightness(1.2)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.filter = 'none'; }}
                    >
                        <img src={shieldIcon} alt="Vetra Verification" style={{ width: '32px', height: 'auto', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.6))' }} />
                    </motion.div>
                </Tooltip>
            )}
        </Box>
    );
};

export default InteractiveVideoPlayer;